/**
 * 월별 독서 통계. 달은 '완료일' 로 나눈다 — 홈의 '올해 N권' 과 같은 기준이다.
 *
 * 무엇을 넣고 뺄지는 실제 기록(2026-09-11)을 보고 정했다.
 *   - 한 달 완독 중앙값이 2권, 74개 달 중 33개가 1권이다. 원그래프·평균은
 *     조각 하나짜리가 되므로 쓰지 않는다.
 *   - 한 달 책의 79% 에 감정 좌표가 있다. MUROO 에만 있는 값이라 쓴다.
 *   - 완독의 50% 가 시작일 = 완료일이다. '읽는 데 걸린 날' 은 절반이 0 이라 뺀다.
 *   - 쪽수는 DB 에 없다.
 */
import { FICTION_GENRES } from "../data/constants";

/* 좌표 화면(EmotionCoordinate)과 같은 축이다. 가로 = bright(0 이 왼쪽),
   세로 = calm(0 이 위). 소설과 비소설은 축의 뜻이 달라 섞지 않는다. */
export const AXES = {
  fiction: {
    x: ["밝음", "어두움"], y: ["차분", "격렬"],
    xAdj: { lo: ["밝고", "밝은"], hi: ["어둡고", "어두운"] },
    yAdj: { lo: "차분한", hi: "격렬한" },
  },
  nonfiction: {
    x: ["가벼움", "무거움"], y: ["실용적", "사색적"],
    xAdj: { lo: ["가볍고", "가벼운"], hi: ["무겁고", "무거운"] },
    yAdj: { lo: "실용적인", hi: "사색적인" },
  },
};

/** "2026.09.08" → "2026-09" */
export const keyOf = (display) => {
  const m = /^(\d{4})\.(\d{2})\./.exec(String(display ?? ""));
  return m ? `${m[1]}-${m[2]}` : null;
};

const prevKey = (k) => {
  let [y, m] = k.split("-").map(Number);
  m -= 1;
  if (m === 0) { m = 12; y -= 1; }
  return `${y}-${String(m).padStart(2, "0")}`;
};

/** 완독이 있는 달, 최근부터 */
export function completedMonths(books, uid) {
  const set = new Set();
  for (const b of books || []) {
    const k = keyOf(b.completedBy?.[uid]);
    if (k) set.add(k);
  }
  return [...set].sort().reverse();
}

/** 좌표 가운데가 어느 쪽인지 한 줄로. 45~55 는 가운데로 본다. */
function readingOf(axes, cx, cy, count) {
  const xs = cx < 45 ? "lo" : cx > 55 ? "hi" : null;
  const ys = cy < 45 ? "lo" : cy > 55 ? "hi" : null;
  let where = null;
  if (xs && ys) where = `${axes.xAdj[xs][0]} ${axes.yAdj[ys]}`;
  else if (xs) where = axes.xAdj[xs][1];
  else if (ys) where = axes.yAdj[ys];
  if (count >= 2) return where ? `${where} 쪽으로 모인 한 달` : "한가운데에 모인 한 달";
  return where ? `${where} 쪽의 한 권` : "한가운데의 한 권";
}

export function statsFor(books, uid, key, quotes = []) {
  const all = books || [];
  const inMonth = all
    .filter((b) => keyOf(b.completedBy?.[uid]) === key)
    .map((b) => ({ ...b, day: String(b.completedBy[uid]).slice(5, 10) }))
    .sort((a, b) => b.day.localeCompare(a.day));
  const prevN = all.filter((b) => keyOf(b.completedBy?.[uid]) === prevKey(key)).length;

  const gc = {};
  for (const b of inMonth) { const g = b.genre || "기타"; gc[g] = (gc[g] || 0) + 1; }
  const genres = Object.entries(gc).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  const ac = {};
  for (const b of inMonth) if (b.author) ac[b.author] = (ac[b.author] || 0) + 1;
  const top = Object.entries(ac).sort((a, b) => b[1] - a[1])[0];
  const topAuthor = top && top[1] >= 2 ? { name: top[0], n: top[1] } : null;

  const fam = { fiction: [], nonfiction: [] };
  for (const b of inMonth) {
    const e = b.emotions?.[uid];
    if (!e) continue;
    fam[FICTION_GENRES.includes(b.genre) ? "fiction" : "nonfiction"].push([e.bright, e.calm]);
  }
  /* 그 달에 더 많은 쪽의 축으로 그린다. 다른 쪽은 몇 권인지만 밝힌다. */
  const pick = fam.fiction.length >= fam.nonfiction.length ? "fiction" : "nonfiction";
  const pts = fam[pick];
  let plane = null;
  if (pts.length) {
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    plane = {
      family: pick, axes: AXES[pick], pts, center: [cx, cy],
      reading: readingOf(AXES[pick], cx, cy, pts.length),
      other: pick === "fiction" ? fam.nonfiction.length : fam.fiction.length,
    };
  }
  const coords = fam.fiction.length + fam.nonfiction.length;

  const quotesN = (quotes || []).filter((q) => String(q.createdAt ?? "").startsWith(key)).length;
  const memosN = all.reduce(
    (s, b) => s + (b.memos || []).filter((mm) => mm.userId === uid && String(mm.createdAtRaw ?? "").startsWith(key)).length,
    0
  );

  /* 이 달부터 거꾸로, 한 권이라도 읽은 달이 끊기지 않고 이어진 수 */
  const has = new Set(completedMonths(all, uid));
  let streak = 0;
  for (let k = key; has.has(k); k = prevKey(k)) streak++;

  const [y, m] = key.split("-").map(Number);
  return { key, y, m, n: inMonth.length, prevN, books: inMonth, genres, topAuthor, plane, coords, quotesN, memosN, streak };
}
